import axios from "axios";
import { NetworkCallPayload } from "./networkHandler.types";
import { reportBaseURL, userBaseURL, expressBaseURL, expressDriverBaseURL, camsServiceURL, apigeeServiceURL } from "../config/Api.config";
import { replaceUrlVariables } from "../utils/updateUrlParams";
import AppConstants from "constants/AppConstants";
import { getCountryUrlByKey } from "utils/urlUtils";
import { filterConflictingHeaders, getStoredHeaders } from "utils/storageUtils";
import { COUNTRY_URL_KEYS } from "constants/CommonConstants";
import isEmpty from "lodash/isEmpty";

const axiosInstance = axios.create();

// Custom config interface to pass additional flags
interface CustomAxiosConfig {
  isUser?: boolean;
  isAuth?: boolean;
  isUAE?: boolean;
  isLogin?: boolean;
  isExpress?: boolean;
  isExpressDriver?: boolean;
  isCamsService?: boolean;
  isApigeeService?: boolean;
}

const setupInterceptors = () => {
  // Request interceptor - handle baseURL and headers
  axiosInstance.interceptors.request.use(
    (config: any) => {
      const customConfig = config as CustomAxiosConfig;
      let serviceType: string | null = null;
      
      // Determine baseURL and service type
      if (customConfig.isExpress || customConfig.isExpressDriver) {
        const ewingsUrl = getCountryUrlByKey(COUNTRY_URL_KEYS.EWINGS);
        config.baseURL = customConfig.isExpressDriver 
          ? (getCountryUrlByKey(COUNTRY_URL_KEYS.EWINGS_DRIVER) || expressDriverBaseURL)
          : (ewingsUrl || expressBaseURL);
        serviceType = customConfig.isExpressDriver ? COUNTRY_URL_KEYS.EWINGS_DRIVER : COUNTRY_URL_KEYS.EWINGS;
      } else if (customConfig.isCamsService) {
        config.baseURL = camsServiceURL;
        serviceType = 'CAMS';
      } else if (customConfig.isApigeeService) {
        config.baseURL = apigeeServiceURL;
        serviceType = 'APIGEE';
      } else {
        const countryUrl = sessionStorage.getItem(AppConstants.COUNTRY_URL);
        config.baseURL = customConfig.isUser 
          ? userBaseURL 
          : (countryUrl && customConfig.isUAE ? countryUrl : reportBaseURL);
        serviceType = COUNTRY_URL_KEYS.WINGS;
      }

      // Initialize headers
      config.headers = config.headers || {};

      // Handle Authorization
      if (customConfig.isAuth) {
        const token = sessionStorage.getItem("token");
        if (token) {
          config.headers['Authorization'] = token;
        }
      }

      // Add system-id 
        config.headers['system-id'] = "CWINGS";
      // Add countryISOCode only for Wings/UAE/Express requests — CAMS and APIGEE do not accept this header
      if (customConfig.isExpress || customConfig.isExpressDriver ||
          (!customConfig.isLogin && !customConfig.isUser && !customConfig.isCamsService && !customConfig.isApigeeService)) {
        const countryCode = sessionStorage.getItem(AppConstants.COUNTRY_CODE);
        if (countryCode) {
          config.headers['countryISOCode'] = countryCode;
        }
      }

      // Merge stored headers based on service type (for non-login, non-user requests)
      if (!customConfig.isLogin && !customConfig.isUser && serviceType) {
        const storedHeaders = getStoredHeaders(serviceType);
        if (storedHeaders  && !isEmpty(storedHeaders)) {
          const filteredHeaders = filterConflictingHeaders(storedHeaders, config.headers);
          config.headers = {
            ...config.headers,
            ...filteredHeaders,
          };
        }
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors and success
  axiosInstance.interceptors.response.use(
    response => {
      // You can add custom success handling here
      // For example, logging, formatting response, etc.
      return response;
    },
    error => {
      // Handle 401 Unauthorized
      if (error.response && error.response.status === 401) {
        const currentPath = window.location.pathname;
        if (currentPath !== "/login") {
          sessionStorage.clear();
          window.location.href = "/login";
        }
      }

      // Handle other status codes if needed
      // if (error.response?.status === 403) {
      //   // Handle forbidden
      // }
      // if (error.response?.status === 500) {
      //   // Handle server error
      // }

      // You can add custom error handling/logging here
      return Promise.reject(error);
    }
  );
};

const networkHandler = async (
  params: NetworkCallPayload,
  options?: CustomAxiosConfig
) => {
  const { url, urlParams, ...restApiOptions } = params;
  let updateUrl = url;
  
  if (urlParams) {
    updateUrl = replaceUrlVariables(url, urlParams);
  }

  // Merge custom config with request options
  const requestConfig = {
    url: updateUrl,
    ...restApiOptions,
    ...options,
  };

  return await axiosInstance.request(requestConfig);
};

// Call setupInterceptors once at application startup
setupInterceptors();

export default networkHandler;